import type { AgentWidget, PresetOption } from "@/types/createAgent";
import { GENERATE_BACK_LABEL, GENERATE_FRONT_LABEL } from "@/lib/agent/labels";
import {
  getCategoryGridActionId,
  getChoicePresets,
  getPresetPresentationType,
  isVibeBundlePreset,
  orderPresets,
} from "@/lib/agent/presetPresentation";
import { normalizeWidgetType } from "@/lib/agent/widgetPlan";
import { getWidgetPresets } from "@/lib/agent/widgetPresets";

/**
 * How a chat choice widget renders, as pure projections.
 *
 * AgentPresets (renderChoiceWidget) and PresetSelectionFooter used to derive
 * this inline, and the terminal harness re-derived it with its own rules. The
 * copies drifted: the harness labelled a widget's secondary footer control
 * from the selected preset's kind ("Refine this", "Explore this direction")
 * while the browser always passes the widget's label ("Continue without
 * generating", or the server-authored `selectedActionLabel`). Both now call
 * these functions.
 */

export type ChoiceWidgetPresetMode = "grid" | "advance" | "choice";

export interface ChoiceWidgetSurface {
  /** Widget presets in display order. */
  presets: PresetOption[];
  choicePresets: PresetOption[];
  /** Catalog grid field, only for explicit catalog widgets or full lists. */
  categoryGridActionId: string | null;
  isBackChoiceGrid: boolean;
  /** Direction cards (VibePresetPicker): explore on tap, no footer. */
  isVibePresetList: boolean;
  /** Upload rows open the file picker directly: no footer. */
  isUploadChoiceList: boolean;
  /** An authored 2-3 option field_picker: one tap advances, no footer. */
  isCuratedOneTapChoice: boolean;
  /** Select-then-footer choices: the PresetSelectionFooter renders. */
  rendersSelectionFooter: boolean;
  /** How each choice tile behaves. */
  presetMode: ChoiceWidgetPresetMode;
  /** Labels AgentPresets passes to PresetSelectionFooter. */
  footerLabels: { generateLabel: string; chatMoreLabel: string };
}

export function projectChoiceWidgetSurface(
  widget: AgentWidget
): ChoiceWidgetSurface {
  const presets = orderPresets(getWidgetPresets(widget));
  const choicePresets = getChoicePresets(presets);
  // Server-authored widget types are authoritative: a focused `choice_set`
  // must NOT expand into the catalog grid just because its presets share one
  // field (docs/create-chat-ui.md). Catalog layout only for explicit catalog
  // widgets or full (>5) lists.
  const widgetType = normalizeWidgetType(widget.type);
  const isExplicitCatalogWidget =
    widgetType === "catalog_picker" || widgetType === "catalog_grid";
  const sharedCategoryActionId = getCategoryGridActionId(choicePresets);
  const categoryGridActionId =
    sharedCategoryActionId &&
    (isExplicitCatalogWidget || choicePresets.length > 5)
      ? sharedCategoryActionId
      : null;
  const isBackChoiceGrid =
    categoryGridActionId === "set_back_prompt" ||
    categoryGridActionId === "set_back_style";
  const isVibePresetList =
    choicePresets.length > 0 && choicePresets.every(isVibeBundlePreset);
  const isUploadChoiceList =
    choicePresets.length > 0 &&
    choicePresets.every(
      preset => getPresetPresentationType(preset) === "upload_choice"
    );
  const isCuratedOneTapChoice =
    widgetType === "field_picker" &&
    !categoryGridActionId &&
    !isUploadChoiceList;
  const rendersSelectionFooter =
    choicePresets.length > 0 &&
    !isVibePresetList &&
    !isUploadChoiceList &&
    !isCuratedOneTapChoice;
  const selectedActionLabel = widget.selection?.data?.selectedActionLabel;
  const generateLabel = widget.selection?.data?.generateLabel;
  return {
    presets,
    choicePresets,
    categoryGridActionId,
    isBackChoiceGrid,
    isVibePresetList,
    isUploadChoiceList,
    isCuratedOneTapChoice,
    rendersSelectionFooter,
    presetMode: categoryGridActionId
      ? "grid"
      : isCuratedOneTapChoice
        ? "advance"
        : "choice",
    footerLabels: choiceFooterLabels({
      isBackChoiceGrid,
      generateLabel: typeof generateLabel === "string" ? generateLabel : null,
      selectedActionLabel:
        typeof selectedActionLabel === "string" ? selectedActionLabel : null,
    }),
  };
}

/**
 * The labels every chat choice footer is given (widget footers and the
 * legacy message-level catalog grid). Server-authored labels win, except that
 * back grids never take the plan-wide (front-biased) Generate label: a commit
 * button must not say "front" and fire generate back.
 */
export function choiceFooterLabels(input: {
  isBackChoiceGrid: boolean;
  generateLabel?: string | null;
  selectedActionLabel?: string | null;
}): { generateLabel: string; chatMoreLabel: string } {
  return {
    generateLabel: input.isBackChoiceGrid
      ? GENERATE_BACK_LABEL
      : (input.generateLabel ?? GENERATE_FRONT_LABEL),
    chatMoreLabel:
      input.selectedActionLabel ??
      (input.isBackChoiceGrid
        ? "Explore back ideas"
        : "Continue without generating"),
  };
}

/**
 * The labels PresetSelectionFooter shows. With nothing picked, the secondary
 * control says the set missed ("None of these fit"): there is no "this" yet.
 */
export function resolveSelectionFooterLabels(input: {
  selectedPreset: PresetOption | null;
  generateLabel?: string;
  chatMoreLabel?: string;
}): { generate: string; chatMore: string } {
  const { selectedPreset } = input;
  const isBackChoice =
    selectedPreset?.actionId === "set_back_prompt" ||
    selectedPreset?.actionId === "set_back_style";
  const isDirectionChoice =
    !!selectedPreset && isVibeBundlePreset(selectedPreset);
  const isSuggestChoice = selectedPreset?.action === "suggest";
  return {
    generate:
      input.generateLabel ??
      (isBackChoice ? GENERATE_BACK_LABEL : GENERATE_FRONT_LABEL),
    chatMore: !selectedPreset
      ? "None of these fit"
      : (input.chatMoreLabel ??
        (isBackChoice
          ? "Explore back ideas"
          : isDirectionChoice
            ? "Explore this direction"
            : isSuggestChoice
              ? "Continue without generating"
              : "Refine this")),
  };
}
